// floatingChatPopup — Phase 6.7 サブ task 後追い (consult 2026-05-07).
//
// 旧版 (Java AWT) では「Enter 2 回連打」で **独立 dialog window** が立ち上がり、
// その小窓だけで発言できた (描画に集中したいとき chat 欄が画面を占有しないため).
//
// Web 流に移植: `window.open()` だと popup blocker 問題があるので、画面右下に
// **absolute positioning の floating panel** で代替する (= Photoshop のフロー
// ティングパレット風). ChatPanel 本体は通常通り表示したまま (= 小窓は追加 UI、
// 本体を hide はしない方針、シンプル).
//
// ## 開閉トリガー (2026-06-12 改訂)
//
// 旧仕様は「空 Enter 2 連打」で開閉していた (= 旧 Java AWT 踏襲) が、
// 「設定画面に列挙される＝発見可能なものを canonical とし、不可視の隠しジェスチャは
// 設けない」原則 (docs `shortcut-settings.md`) に従い、Enter 2 連打 (開く側・閉じる側
// とも) を撤去した. 開く経路は **Ctrl/Cmd+Enter 一本** (= ショートカット設定画面に
// 出る `chat.openPopup`、`isOpenPopupHotkey`)、閉じる経路は **× ボタン / Esc** とする
// (= 標準モーダル慣例なので隠しジェスチャではない). 作者判断で Enter 2 連打は
// ほぼ未使用と確認済 (2026-06-12 撤去).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM**. React component や
// PointerEvent を node 環境で素直に組み立てられないため、
// (1) Ctrl/Cmd+Enter hotkey 判定 (`isOpenPopupHotkey`)
// (2) ドラッグ移動の position 計算
// を純粋関数に切り出して単体テスト可能にする. ChatPanel.tsx 側はこれらを組み合
// わせるだけで自前のテストは不要.
//
// (architecture.md §16, classic-ui-restore consult 2026-05-07)

// ─── 小窓 open / close 状態 ────────────────────────────────────────────────

/**
 * 小窓の表示状態 (= ChatPanel 側で `useState` する).
 *
 * - `'closed'`: 表示していない.
 * - `'open'`: 表示中.
 *
 * 設計の核心 (consult 2026-05-07): 状態遷移が単純な 2 値なので boolean で
 * 十分だが、可読性のために discriminated union にする (= 将来「最小化」状態を
 * 追加するときの拡張に備える).
 */
export type FloatingPopupState = 'closed' | 'open';

/**
 * 状態遷移の純粋ヘルパ (toggle, `'closed'` ⇄ `'open'`).
 *
 * 現状の開閉経路 (Ctrl/Cmd+Enter open + ×/Esc close) はいずれも方向が確定して
 * いるので open/close 専用 helper を使い、本 toggle は実コードからは未使用.
 * 将来「同一トリガーで開閉トグル」する UI を足すときの拡張点として残す.
 *
 * 注意 (consult 2026-05-07 バグ修正): 「閉じる」専用の経路 (× ボタン / Esc) は
 * `closePopupState` を、「開く」専用の経路 (Ctrl/Cmd+Enter hotkey) は
 * `openPopupState` を使う. toggle と open/close を混ぜて使うと「× を押しても
 * 閉じない」バグが起きうるため (= idempotent open setter が close 経路に流用
 * された過去の事故を踏まえ helper を分離).
 */
export const togglePopupState = (cur: FloatingPopupState): FloatingPopupState =>
  cur === 'closed' ? 'open' : 'closed';

/**
 * 「開く」専用の状態遷移ヘルパ (idempotent).
 *
 * - `'closed'` → `'open'`: 開く.
 * - `'open'` → `'open'`: 既に開いているので何もしない (idempotent).
 *
 * Ctrl+Enter / Cmd+Enter (= 旧版 `WhiteboardCanvas.java:2057` 踏襲) の hotkey
 * 経路で使う (= 2026-06-12 以降は唯一の open 経路). 「開くのみ」のセマンティクスを
 * 1 関数に閉じ込めることで、「× ボタン」など close 経路に誤って同じロジックが
 * 流用されないようにする.
 */
export const openPopupState = (cur: FloatingPopupState): FloatingPopupState =>
  cur === 'closed' ? 'open' : cur;

/**
 * 「閉じる」専用の状態遷移ヘルパ (常に `'closed'`).
 *
 * - `'open'` → `'closed'`.
 * - `'closed'` → `'closed'` (no-op).
 *
 * × ボタン / Esc (= popup は open のときだけ render されるので実質 always close)
 * で使う. **toggle ではなく必ず `'closed'` を返す**ことで、
 * もし state がレースで二重更新されても確実に閉じる挙動になる. 旧版踏襲では
 * close 経路は常に「閉じる」だけを意味し、「閉じてるなら開く」みたいな両義
 * 挙動は持たない方針 (consult 2026-05-07 バグ修正).
 */
export const closePopupState = (_cur: FloatingPopupState): FloatingPopupState => 'closed';

/**
 * 「小窓 input から送信した」直後の状態遷移を計算する純粋ヘルパ
 * (consult 2026-05-07 後追い、旧版 `OekakiChatJava/.../ChatPanel.java:614-617` 踏襲).
 *
 * 旧版は `textInput` (本体) / `textInput2` (発言小窓) 共通の
 * `textInputAction()` 経路で、空でない送信時に `setChatInputWindowVisible(false)`
 * を呼んで小窓を閉じていた. 新版は popup 内 input の Enter 送信後 popup が
 * 開いたままになる挙動だったので、`submitted === true` (= `submitText` が
 * 何らかの処理を行った) なら `'closed'` へ、`false` (= ROM ガード等で未送信、
 * 入力欄に文字が残ってる) なら現状維持に倒す.
 *
 * - 空送信 (= `submitText` が `trimmed.length === 0` で早期 return false)
 *   → `submitted === false` でこのヘルパは `cur` を返す. 「小窓を閉じる」
 *     経路は × ボタン / Esc (= `closePopupState` を直接呼ぶ) が担当.
 * - 非空送信が成功 → `submitted === true` で `'closed'` を返す.
 * - 非空だが送信失敗 (ROM 中 + 非 slash 等) → `submitted === false`、popup は
 *   開いたまま (= ユーザが訂正できる).
 */
export const popupStateAfterSubmit = (
  cur: FloatingPopupState,
  submitted: boolean,
): FloatingPopupState => (submitted ? 'closed' : cur);

// ─── ドラッグ移動の position 計算 ─────────────────────────────────────────

/** 小窓の画面上の位置 (= ChatPanel 側で `useState` する). 単位: px. */
export interface FloatingPopupPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * ドラッグ開始時に保存しておく "アンカー" (= origin 位置 + pointer 開始位置).
 *
 * `onPointerDown` で `{ origin: cur position, pointerStart: client coords }` を
 * 記録し、`onPointerMove` で「現在 pointer 座標 - pointerStart + origin」を
 * 新しい位置にする (= ドラッグ中ジャンプしないようにする標準パターン).
 */
export interface FloatingDragAnchor {
  readonly origin: FloatingPopupPosition;
  readonly pointerStart: { readonly x: number; readonly y: number };
}

/**
 * pointermove の clientX/Y からドラッグ後の新しい position を計算する純粋関数.
 *
 * caller は `onPointerMove(e)` で `e.clientX / e.clientY` を渡す. anchor は
 * `onPointerDown` 時に作って ref / state に保持する. viewport 範囲外への
 * はみ出しは caller (= ChatPanel 側) が必要なら clamp する設計だが、
 * 簡素化のためここでは clamp しない (= 旧版 dialog window もはみ出し可能).
 */
export const nextPositionFromDrag = (
  anchor: FloatingDragAnchor,
  pointer: { readonly x: number; readonly y: number },
): FloatingPopupPosition => ({
  x: anchor.origin.x + (pointer.x - anchor.pointerStart.x),
  y: anchor.origin.y + (pointer.y - anchor.pointerStart.y),
});

/**
 * floating popup の位置を viewport 内に収める純粋関数 (2026-05-27 追加).
 *
 * `nextPositionFromDrag` は単純加算で clamp を持たない (= 上のコメント通り
 * 「caller が必要なら clamp する」設計). 各 floating panel (カラーホイール /
 * レイヤー / プレビュー / 発言小窓) が掴んで画面外へ出せてしまうのを防ぐため、
 * ドラッグ後の位置・window resize 後の位置をこの関数に通して画面内へ引き戻す.
 *
 * 各軸独立に
 *   `min(max(pos, margin), max(margin, viewport - panel - margin))`
 * で clamp する:
 * - 下限は `margin` (= 画面左/上端に margin だけ残す).
 * - 上限は `viewport - panel - margin` (= 画面右/下端に margin だけ残す),
 *   ただし `max(margin, ...)` でガードするので、パネルが viewport より大きい
 *   ケース (= `viewport - panel - margin < margin`) でも上限が `margin` に
 *   なり、結果として位置は `margin` に張り付く. これにより **左上 (= ドラッグ
 *   可能なタイトルバーがある角) が必ず画面内に残る** (= 掴んで戻せる).
 *
 * `panelSize` は各 component が `getBoundingClientRect()` で実測した値を渡す
 * 前提 (= ハードコード幅に依存しない). zoom 適用後の panel でも
 * `getBoundingClientRect` は zoom 後の実効サイズを返すのでそのまま使える.
 */
export const clampPopupPosition = (
  pos: FloatingPopupPosition,
  panelSize: FloatingPopupSize,
  viewport: { readonly width: number; readonly height: number },
  margin = 8,
): FloatingPopupPosition => ({
  x: Math.min(Math.max(pos.x, margin), Math.max(margin, viewport.width - panelSize.width - margin)),
  y: Math.min(
    Math.max(pos.y, margin),
    Math.max(margin, viewport.height - panelSize.height - margin),
  ),
});

// ─── floating frame のリサイズ計算 (chat detach popup 用, 2026-05-17 追加) ───
//
// 旧版 (Java AWT) の `TextChatFrame` は `JDialog` だったので OS が標準で resize
// gizmo を提供していた. 新版は `position: fixed` の DIV なので自前で右下に
// resize handle を出して `pointerdown -> move` で width/height を更新する.
// pure helper として外出ししたのは `nextPositionFromDrag` と同じ理由 (= vitest
// node env でテストするため + min clamp ロジックを 1 箇所に集約するため).

/** 浮動 frame のサイズ (= ClassicLayout 側で `useState` する). 単位: px. */
export interface FloatingPopupSize {
  readonly width: number;
  readonly height: number;
}

/**
 * リサイズ開始時に保存しておく "アンカー" (= origin サイズ + pointer 開始位置).
 *
 * `onPointerDown` で `{ origin: cur size, pointerStart: client coords }` を
 * 記録し、`onPointerMove` で「origin + (pointer delta)」を新サイズにする
 * (= ドラッグ中ジャンプしないようにする標準パターン, `FloatingDragAnchor` と
 * 並行).
 */
export interface FloatingResizeAnchor {
  readonly origin: FloatingPopupSize;
  readonly pointerStart: { readonly x: number; readonly y: number };
}

/**
 * チャット detach popup の **最小サイズ**. 旧版 dialog 400x200 を参考に、新版は
 * member list (140px) を内包するため幅をやや広めに設定. これ以下に縮めると
 * 入力欄やメンバー名が極端に潰れて使い物にならないため pure helper で clamp する.
 */
export const FLOATING_CHAT_MIN_WIDTH = 360;
export const FLOATING_CHAT_MIN_HEIGHT = 180;

/**
 * pointermove の clientX/Y からリサイズ後の新しい size を計算する純粋関数.
 *
 * 仕様:
 * - 右下 resize handle を引いた場合: 新 width = origin.width + (pointer.x -
 *   pointerStart.x), 新 height = origin.height + (pointer.y - pointerStart.y).
 * - 最小サイズ (`FLOATING_CHAT_MIN_WIDTH` / `FLOATING_CHAT_MIN_HEIGHT`) で
 *   clamp. 引数の `min` で上書き可能 (テスト容易性のため).
 * - 最大サイズは clamp しない (= viewport をはみ出した場合は caller が判断、
 *   `nextPositionFromDrag` と同じ方針).
 */
export const nextSizeFromResize = (
  anchor: FloatingResizeAnchor,
  pointer: { readonly x: number; readonly y: number },
  min: FloatingPopupSize = { width: FLOATING_CHAT_MIN_WIDTH, height: FLOATING_CHAT_MIN_HEIGHT },
): FloatingPopupSize => ({
  width: Math.max(min.width, anchor.origin.width + (pointer.x - anchor.pointerStart.x)),
  height: Math.max(min.height, anchor.origin.height + (pointer.y - anchor.pointerStart.y)),
});

// ─── 枠ドラッグ (Windows 風 8 方向) リサイズ (chat-popup-edge-resize, 2026-06-11) ──
//
// 右下グリップ 1 個 (`nextSizeFromResize`) を OS ウィンドウ風の「パネル周囲の
// 透明判定ゾーン (4 辺 + 4 隅)」に拡張する. プレビュー窓の `nextPreviewResize`
// と同じ UX だが、**プレビューはアスペクト比固定の edge スカラ 1 本**なのに対し、
// チャット detach 窓は **width / height 独立の自由 2D リサイズ** なので、edge
// スカラ方式ではなく「各辺は 1 次元、各隅は 2 次元」を素直に動かす 2D 版にする.
//
// 各ゾーンで「掴んだ辺/隅の反対側 (= 対辺 / 対角) を画面上の固定点」にして
// 伸縮させる. min clamp 後も固定点が動かないよう、width/height を確定してから
// 固定点基準で x/y を逆算する (= 標準的な 2D 窓リサイズ).
//
// | zone | 水平: width / x                 | 垂直: height / y                |
// |------|---------------------------------|---------------------------------|
// | e    | w=ow+dx,    x 不動 (左固定)      | 据え置き                        |
// | w    | w=ow-dx,    左端=右端固定で逆算  | 据え置き                        |
// | s    | 据え置き                        | h=oh+dy,    y 不動 (上固定)      |
// | n    | 据え置き                        | h=oh-dy,    上端=下端固定で逆算  |
// | se   | e + s (右下、左上固定)                                               |
// | sw   | w + s (左下、右上固定)                                               |
// | ne   | e + n (右上、左下固定)                                               |
// | nw   | w + n (左上、右下固定)                                               |

/** 8 方向リサイズゾーンの識別子 (4 辺 + 4 隅). */
export type FloatingResizeZone = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * 2D・8 方向リサイズ開始時に保存しておく "アンカー".
 *
 * - `originPos`: pointerdown 時の panel 左上 (= 固定点逆算の基準).
 * - `originSize`: pointerdown 時の panel 実寸 (= width/height の起点).
 * - `pointerStart`: pointerdown 時の client 座標.
 *
 * 右下のみだった `FloatingResizeAnchor` (size + pointerStart) の 2D 拡張版で、
 * west/north ゾーンで固定点 (= 右端 / 下端) を逆算するために origin の位置も持つ.
 */
export interface Floating2DResizeAnchor {
  readonly originPos: FloatingPopupPosition;
  readonly originSize: FloatingPopupSize;
  readonly pointerStart: { readonly x: number; readonly y: number };
}

/**
 * 8 方向 (4 辺 + 4 隅) の枠ドラッグから新しい size + panel 左上 (x, y) を計算する
 * 純粋関数 (= width/height 独立、アスペクト非固定).
 *
 * 仕様 (chat-popup-edge-resize):
 * - `dx = pointer.x - pointerStart.x`, `dy = pointer.y - pointerStart.y`.
 * - zone を水平成分 (east: e/ne/se, west: w/nw/sw, 無し: n/s) と垂直成分
 *   (south: s/se/sw, north: n/ne/nw, 無し: e/w) に分解して各軸を独立に動かす.
 * - east: `width = max(min.w, originSize.w + dx)`, `x = originPos.x` (左固定).
 *   west: `width = max(min.w, originSize.w - dx)`, `x = originPos.x +
 *   (originSize.w - width)` (= 右端固定. min clamp で width が縮みきっても
 *   右端 `originPos.x + originSize.w` が動かない).
 *   水平成分無し: width/x は据え置き (= originSize.w / originPos.x).
 * - south: `height = max(min.h, originSize.h + dy)`, `y = originPos.y` (上固定).
 *   north: `height = max(min.h, originSize.h - dy)`, `y = originPos.y +
 *   (originSize.h - height)` (= 下端固定).
 *   垂直成分無し: height/y は据え置き.
 *
 * se は従来 `nextSizeFromResize` と同じ「左上固定・size のみ増減」になる
 * (= x/y 不動). e は dy を無視、s は dx を無視する (= 単軸).
 */
export const nextFloatingResize = (
  zone: FloatingResizeZone,
  anchor: Floating2DResizeAnchor,
  pointer: { readonly x: number; readonly y: number },
  min: FloatingPopupSize = { width: FLOATING_CHAT_MIN_WIDTH, height: FLOATING_CHAT_MIN_HEIGHT },
): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } => {
  const { originPos, originSize, pointerStart } = anchor;
  const dx = pointer.x - pointerStart.x;
  const dy = pointer.y - pointerStart.y;

  const isEast = zone === 'e' || zone === 'ne' || zone === 'se';
  const isWest = zone === 'w' || zone === 'nw' || zone === 'sw';
  const isSouth = zone === 's' || zone === 'se' || zone === 'sw';
  const isNorth = zone === 'n' || zone === 'ne' || zone === 'nw';

  // ── 水平軸 (width / x) ──
  let width: number;
  let x: number;
  if (isEast) {
    width = Math.max(min.width, originSize.width + dx);
    x = originPos.x;
  } else if (isWest) {
    width = Math.max(min.width, originSize.width - dx);
    // 右端 (originPos.x + originSize.width) を固定点に逆算.
    x = originPos.x + (originSize.width - width);
  } else {
    width = originSize.width;
    x = originPos.x;
  }

  // ── 垂直軸 (height / y) ──
  let height: number;
  let y: number;
  if (isSouth) {
    height = Math.max(min.height, originSize.height + dy);
    y = originPos.y;
  } else if (isNorth) {
    height = Math.max(min.height, originSize.height - dy);
    // 下端 (originPos.y + originSize.height) を固定点に逆算.
    y = originPos.y + (originSize.height - height);
  } else {
    height = originSize.height;
    y = originPos.y;
  }

  return { x, y, width, height };
};

// ─── Ctrl+Enter / Cmd+Enter で発言小窓を開く判定 ────────────────────────────
//
// 旧版 (Java AWT) の `WhiteboardCanvas.keyPressed` (line 2057) では canvas
// focus 時の Ctrl+Enter で発言小窓 dialog を開いていた. 新版は document-level
// listener として実装する (= ChatPanel input にいてもキャンバスにいても、修飾
// キー入りの Enter は明示的な「発言小窓を開く」action として一貫処理).
//
// ## なぜ pure helper として外出ししたか
//
// (1) Enter 2 連打判定と同様 vitest が node env で DOM を持たないため, raw
//     KeyboardEvent を直接アサートできない. boolean を返す純粋関数なら
//     入力 object を mock するだけで test できる.
// (2) Ctrl / Cmd / IME 状態の OR / 否定の組合せを 1 箇所に集約することで、
//     hook 内の listener が「真に hotkey か」を 1 行で判定できる (= 読みやすい).
//
// ⚠ スライス3 (2026-06-12) で `chat.openPopup` はリバインド可能化され、発火は
// `useLayoutKeyboardShortcuts` の generic chord 経路 (`Ctrl+Enter` / `Meta+Enter` の
// 2 binding + `!e.isComposing` IME ガード) が担う. `isOpenPopupHotkey` はもう配線から
// 呼ばれず、判定仕様のリファレンス + 単体テスト用に残置している.

/** Ctrl+Enter / Cmd+Enter 検出に必要な KeyboardEvent の最小サブセット. */
export interface OpenPopupHotkeyInput {
  /** `KeyboardEvent.key`. Enter なら `'Enter'`. */
  readonly key: string;
  /** Win/Linux 想定. `KeyboardEvent.ctrlKey`. */
  readonly ctrlKey: boolean;
  /** Mac の Cmd キー. `KeyboardEvent.metaKey`. */
  readonly metaKey: boolean;
  /** IME 変換中なら `true`. このとき hotkey 判定は false に倒す. */
  readonly isComposing: boolean;
}

/**
 * Ctrl+Enter / Cmd+Enter (= 発言小窓を開く hotkey) かどうかを判定する純粋関数.
 *
 * 仕様 (consult 2026-05-07, 旧版 `WhiteboardCanvas.java:2057` 踏襲):
 * - `key === 'Enter'` かつ (`ctrlKey === true` または `metaKey === true`) なら true.
 * - IME 変換中 (`isComposing === true`) は false (= 日本語入力 確定の Enter と
 *   競合しない).
 * - 修飾キー無しの Enter / Enter 以外のキーは false (= 通常の chat 送信や Enter
 *   2 連打判定の経路に干渉しない).
 *
 * caller は document-level keydown で `e.preventDefault()` を呼んでから
 * 「発言小窓を開く」action を実行する.
 */
export const isOpenPopupHotkey = (input: OpenPopupHotkeyInput): boolean => {
  if (input.isComposing) return false;
  if (input.key !== 'Enter') return false;
  return input.ctrlKey || input.metaKey;
};
